import type { CeleryState, CeleryTaskResponse } from "../api/celery/types";

export type MockDataScenario = "empty" | "failures" | "busy" | "mixed";

const baseMockStats = {
	queue_length: 0,
	active_workers: 1,
	reserved_tasks: 0,
	active_tasks: 0,
	pending_tasks: 0,
	per_queue: {
		default: {
			active_tasks: 0,
			reserved_tasks: 0,
			workers: 1,
			pending_tasks: 0,
		},
		ml: {
			active_tasks: 0,
			reserved_tasks: 0,
			workers: 0,
			pending_tasks: 0,
		},
		reindex: {
			active_tasks: 0,
			reserved_tasks: 0,
			workers: 0,
			pending_tasks: 0,
		},
	},
};

const mockTask = (
	id: string,
	name: string,
	state: CeleryState,
	worker: string = "jimmy-rog",
	kwargs?: string
): CeleryTaskResponse => ({
	uuid: id,
	name,
	state,
	received: new Date(Date.now() - Math.random() * 3600 * 1000).toISOString(),
	args: "[]",
	kwargs: kwargs || "{}",
	retries: state === "FAILURE" ? Math.floor(Math.random() * 3) : 0,
	result:
		state === "FAILURE"
			? "Exception: Task failed"
			: state === "SUCCESS"
				? "Task completed"
				: "",
	clock: 1,
	celery_root: "",
	celery_root_id: "",
	worker,
	started: new Date(Date.now() - Math.random() * 1800 * 1000).toISOString(),
	traceback:
		state === "FAILURE"
			? 'Traceback (most recent call last):\n  File "..."\n    raise Exception("Task failed")'
			: undefined,
	timestamp: new Date().toISOString(),
	runtime: state === "SUCCESS" ? Math.random() * 30 : undefined,
});

export const mockCeleryData: Record<
	MockDataScenario,
	{
		stats: typeof baseMockStats;
		tasks: CeleryTaskResponse[];
	}
> = {
	empty: {
		stats: baseMockStats,
		tasks: [],
	},

	failures: {
		stats: {
			...baseMockStats,
			pending_tasks: 12,
			per_queue: {
				...baseMockStats.per_queue,
				default: {
					...baseMockStats.per_queue.default,
					pending_tasks: 8,
				},
				ml: {
					...baseMockStats.per_queue.ml,
					pending_tasks: 4,
				},
			},
		},
		tasks: [
			mockTask(
				"failure-1",
				"send_linkedin_message",
				"FAILURE" as CeleryState,
				"jimmy-rog",
				"{'person_id': 12345, 'template_id': 'intro_v2'}"
			),
			mockTask(
				"failure-2",
				"rebuild_gcq",
				"FAILURE" as CeleryState,
				"jimmy-rog",
				"{'queue_name': 'default', 'force': True}"
			),
			mockTask(
				"failure-3",
				"process_email_bounce",
				"FAILURE" as CeleryState,
				"jimmy-rog",
				"{'email_id': 789, 'bounce_type': 'hard'}"
			),
			mockTask(
				"failure-4",
				"run_ml_prediction",
				"FAILURE" as CeleryState,
				"jimmy-rog",
				"{'assignment_id': 364, 'user_id': None, 'mode': 11}"
			),
			mockTask(
				"failure-5",
				"update_person_skills",
				"FAILURE" as CeleryState,
				"jimmy-rog",
				"{'person_id': 55678, 'skills': ['Python', 'React']}"
			),
		],
	},

	busy: {
		stats: {
			...baseMockStats,
			queue_length: 25,
			active_tasks: 8,
			reserved_tasks: 12,
			per_queue: {
				default: {
					active_tasks: 3,
					reserved_tasks: 8,
					workers: 1,
					pending_tasks: 0,
				},
				ml: {
					active_tasks: 3,
					reserved_tasks: 2,
					workers: 2,
					pending_tasks: 0,
				},
				reindex: {
					active_tasks: 2,
					reserved_tasks: 2,
					workers: 1,
					pending_tasks: 0,
				},
			},
		},
		tasks: [
			mockTask(
				"active-1",
				"send_linkedin_message",
				"STARTED" as CeleryState,
				"jimmy-rog",
				"{'person_id': 98765, 'message_type': 'follow_up'}"
			),
			mockTask(
				"active-2",
				"process_email_queue",
				"STARTED" as CeleryState,
				"jimmy-rog",
				"{'queue_size': 150, 'batch_size': 25}"
			),
			mockTask(
				"active-3",
				"run_ml_prediction",
				"STARTED" as CeleryState,
				"jimmy-rog",
				"{'model_id': 'v2.1', 'dataset_size': 5000}"
			),
			mockTask(
				"pending-1",
				"rebuild_search_index",
				"PENDING" as CeleryState,
				"jimmy-rog",
				"{'index_type': 'full', 'force_rebuild': True}"
			),
			mockTask(
				"pending-2",
				"sync_person_data",
				"PENDING" as CeleryState,
				"jimmy-rog",
				"{'source': 'linkedin', 'updated_since': '2025-01-01'}"
			),
			mockTask(
				"pending-3",
				"generate_report",
				"PENDING" as CeleryState,
				"jimmy-rog",
				"{'report_type': 'weekly', 'format': 'pdf'}"
			),
			mockTask(
				"retry-1",
				"send_invitation",
				"RETRY" as CeleryState,
				"jimmy-rog",
				"{'person_id': 11223, 'retry_count': 2}"
			),
			mockTask(
				"retry-2",
				"process_webhook",
				"RETRY" as CeleryState,
				"jimmy-rog",
				"{'webhook_id': 445566, 'event_type': 'user.updated'}"
			),
		],
	},

	mixed: {
		stats: {
			...baseMockStats,
			queue_length: 15,
			active_tasks: 3,
			reserved_tasks: 5,
			pending_tasks: 7,
			per_queue: {
				default: {
					active_tasks: 2,
					reserved_tasks: 3,
					workers: 1,
					pending_tasks: 4,
				},
				ml: {
					active_tasks: 1,
					reserved_tasks: 2,
					workers: 1,
					pending_tasks: 3,
				},
				reindex: {
					active_tasks: 0,
					reserved_tasks: 0,
					workers: 0,
					pending_tasks: 0,
				},
			},
		},
		tasks: [
			mockTask(
				"failure-1",
				"rebuild_gcq",
				"FAILURE" as CeleryState,
				"jimmy-rog",
				"{'assignment_id': 364, 'user_id': None, 'mode': 11}"
			),
			mockTask(
				"failure-2",
				"send_email_batch",
				"FAILURE" as CeleryState,
				"jimmy-rog",
				"{'template_id': 'reminder', 'recipient_count': 500}"
			),
			mockTask(
				"active-1",
				"process_linkedin_data",
				"STARTED" as CeleryState,
				"jimmy-rog",
				"{'person_ids': [1001, 1002, 1003], 'update_skills': True}"
			),
			mockTask(
				"active-2",
				"run_ml_evaluation",
				"STARTED" as CeleryState,
				"jimmy-rog",
				"{'model_version': '3.2', 'test_set_id': 'eval_2025_q1'}"
			),
			mockTask(
				"pending-1",
				"sync_database",
				"PENDING" as CeleryState,
				"jimmy-rog",
				"{'tables': ['persons', 'companies'], 'full_sync': False}"
			),
			mockTask(
				"pending-2",
				"generate_weekly_report",
				"PENDING" as CeleryState,
				"jimmy-rog",
				"{'week_start': '2025-01-06', 'include_charts': True}"
			),
			mockTask(
				"retry-1",
				"send_invitation_batch",
				"RETRY" as CeleryState,
				"jimmy-rog",
				"{'batch_id': 'inv_20250113', 'retry_failed': True}"
			),
			mockTask(
				"success-1",
				"backup_database",
				"SUCCESS" as CeleryState,
				"jimmy-rog",
				"{'backup_type': 'incremental', 'compression': 'gzip'}"
			),
		],
	},
};
